import { Link } from 'react-router-dom'
import { Github, Linkedin, Mail, Twitter } from 'lucide-react'

import { BrandLockup } from '@components/brand'
import { usePrivacyPreferences } from '@contexts/PrivacyPreferencesContext'

const LINK_GROUPS = [
  {
    title: 'Platform',
    links: [
      { label: 'Capabilities', to: '/features' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Developer guide', to: '/developers' },
      { label: 'Documentation', docs: true },
      { label: 'Trading workspace', href: import.meta.env.VITE_DASHBOARD_URL }
    ]
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Careers', to: '/careers' },
      { label: 'Insights', to: '/blog' },
      { label: 'Contact', to: '/contact' }
    ]
  },
  {
    title: 'Trust',
    links: [
      { label: 'Security', to: '/security' },
      { label: 'Compliance', to: '/compliance' },
      { label: 'Privacy', to: '/privacy' },
      { label: 'Cookies', to: '/cookies' },
      { label: 'Cookie settings', settings: true },
      { label: 'Terms', to: '/terms' }
    ]
  }
]

const SOCIAL_LINKS = [
  { label: 'X', href: 'https://twitter.com/x3algo', Icon: Twitter },
  { label: 'GitHub', href: 'https://github.com/x3algo', Icon: Github },
  { label: 'LinkedIn', href: 'https://linkedin.com/company/x3algo', Icon: Linkedin },
  { label: 'Email', href: 'mailto:contact@x3algo.com', Icon: Mail }
]

function FooterLink({ link, docsUrl, onOpenPrivacySettings }) {
  const classes = 'text-sm text-white/65 transition-colors hover:text-white'

  if (link.settings) {
    return (
      <button type="button" onClick={onOpenPrivacySettings} className={classes}>
        {link.label}
      </button>
    )
  }

  if (link.docs) {
    return <a href={docsUrl} className={classes}>Documentation</a>
  }

  if (link.to) {
    return <Link to={link.to} className={classes}>{link.label}</Link>
  }

  return <a href={link.href} className={classes}>{link.label}</a>
}

function Footer() {
  const { openSettings } = usePrivacyPreferences()
  const isLocal = import.meta.env.DEV || window.location.hostname === 'localhost'
  const docsUrl = isLocal ? 'http://localhost:3002/docs/' : '/docs/'

  return (
    <footer className="bg-[#121417] text-white print:hidden">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-[1.4fr_repeat(3,1fr)]">
          <div>
            <Link to="/" aria-label="x3Algo home" className="inline-flex">
              <BrandLockup size="md" inverse />
            </Link>
            <p className="mt-5 max-w-xs text-sm leading-6 text-white/65">
              Build, backtest, and paper-deploy algorithmic trading strategies with visible controls.
            </p>
            <div className="mt-6 flex items-center gap-2">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-md text-white/60 hover:bg-white/10 hover:text-white"
                  aria-label={label}
                  title={label}
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {LINK_GROUPS.map((group) => (
            <div key={group.title}>
              <h2 className="text-sm font-semibold text-white">{group.title}</h2>
              <ul className="mt-4 space-y-3">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <FooterLink
                      link={link}
                      docsUrl={docsUrl}
                      onOpenPrivacySettings={openSettings}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 border-t border-white/10 pt-7 sm:flex sm:items-start sm:justify-between sm:gap-8">
          <p className="text-sm text-white/55">
            © {new Date().getFullYear()} x3Algo. All rights reserved.
          </p>
          <p className="mt-3 max-w-xl text-sm leading-6 text-white/55 sm:mt-0 sm:text-right">
            Trading and investing involve risk. Product availability depends on broker,
            exchange, account, and regulatory permissions.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
